import { LOCALES, getBrowserLocale } from '../i18n';
import { APP_CONFIG_PREFIX } from '../config';
import File from './File';

const KEY_TOKEN  = `${APP_CONFIG_PREFIX}_TOKEN`;
const KEY_LOCALE = `${APP_CONFIG_PREFIX}_LOCALE`;
const KEY_QUEUEMONITOR = `${APP_CONFIG_PREFIX}_QUEUEMONITOR`;

const FILE_NAME_FIRST_LAUNCH = `.${APP_CONFIG_PREFIX}_FIRST_LAUNCH`;

const { localStorage } = window;

class Storage {

    constructor() {
        this.secureStorage = undefined;
        this.token = undefined;
        this.locale = undefined;
        this.queueMonitor = undefined;

        this.loadLocale();
    }

    /**
     * Initializes the secure storage. Can be called multiple times, but
     * returns always the same pending or resolved promise. This function
     * also loads the token of the SecureStorage or localStorage.
     * @returns {Promise}
     */
    init() {
        return this.initSecureStorage()
            .then(() => this.unsetTokenOnFirstLaunch())
            .then(() => this.loadToken())
            .then(() => this.loadQueueMonitor());
    }

    /**
     * Initializes the SecureStorage plugin.
     * @returns {Promise} Promise which always resolves
     */
    initSecureStorage() {
        return new Promise(resolve => {
            document.addEventListener('deviceready', () => {
                try {
                    const SecureStorage = Storage.getPlugin();

                    this.secureStorage = new SecureStorage(
                        () => {
                            console.log('Storage: Service initialized');
                            resolve();
                        },
                        err => {
                            console.log(`Storage: Error initializing service: ${err}`);
                            this.secureStorage = null;
                            resolve();
                        },
                        'HolApp' // namespace
                    );
                } catch (err) {
                    console.log(`Storage: Error initializing service: ${err}`);
                    this.secureStorage = null;
                    resolve();
                }
            });
        });
    }

    /**
     * Get the Cordova SecureStorage plugin constructor.
     * @returns {null|*}
     */
    static getPlugin() {
        const { cordova } = window;
        if (Storage.isMobile()
            && cordova.plugins && cordova.plugins.SecureStorage) {
            return cordova.plugins.SecureStorage;
        } else {
            throw Error(`Storage: SecureStorage plugin for platform ${cordova.platformId} not available`);
        }
    }

    /**
     * Checks if we are on a mobile device.
     * @return {boolean} True if a mobile device, otherwise false.
     */
    static isMobile() {
        const { cordova } = window;
        return !!cordova && (cordova.platformId === 'ios' || cordova.platformId === 'android');
    }

    /**
     * Checks if we are in a browser.
     */
    static isBrowser() {
        const { cordova } = window;
        return !!cordova && cordova.platformId === 'browser';
    }

    /**
     * Checks if the secure storage is available. Can be used if we are on a mobile, and the
     * secure storage has been initialized without errors.
     * @return {boolean} True if secure storage can be used, otherwise false.
     */
    hasSecureStorage() {
        return !!this.secureStorage;
    }

    /**
     * Checks if the localStorage is available. Can be used if we are in a browser, the secure
     * storage is not available but the localStorage object is available.
     * @return {boolean} True if localStorage can be used, otherwise false.
     */
    static hasLocalStorage() {
        try {
            const key = Math.random().toString();
            const val = '__storage_test__';

            localStorage.setItem(key, val);
            localStorage.removeItem(key);

            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Set a local storage item.
     * @param key
     * @param value
     * @return {Promise}
     */
    setLocalStorageItem(key, value) {
        return new Promise(resolve => {
            localStorage.setItem(key, value);
            resolve();
        });
    }

    /**
     * Set a secure storage item.
     * @param key
     * @param value
     * @returns {Promise}
     */
    setSecureStorageItem(key, value) {
        return new Promise((resolve, reject) => {
            this.secureStorage.set(
                () => {
                    console.log(`Storage: Successfully set ${key} in secure storage`);
                    resolve();
                },
                err => {
                    console.log(`Storage: Failed saving ${key} in secure storage: ${err}`);
                    reject(err);
                },
                key,
                value
            );
        });
    }

    /**
     * Get the local storage item.
     * @param key
     * @returns {Promise}
     */
    getLocalStorageItem(key) {
        return new Promise(resolve => {
            resolve(localStorage.getItem(key));
        });
    }

    /**
     * Get a secure storage item.
     * @param key
     * @returns {Promise}
     */
    getSecureStorageItem(key) {
        return new Promise(resolve => {
            this.secureStorage.get(
                val => {
                    console.log(`Storage: Successfully read ${key} from secure storage`);
                    resolve(val);
                },
                err => {
                    console.log(`Storage: Failed reading ${key} from secure storage: ${err}`);
                    // don't fail, localStorage returns 'null' for unkown key
                    resolve(null);
                },
                key
            );
        });
    }

    removeLocalStorageItem(key) {
        return new Promise(resolve => {
            localStorage.removeItem(key);
            resolve();
        });
    }

    /**
     * Removes a secure storage item.
     * @param key
     * @returns {Promise}
     */
    removeSecureStorageItem(key) {
        return new Promise((resolve, reject) => {
            this.secureStorage.remove(
                () => {
                    console.log(`Storage: Successfully removed ${key} from secure storage`);
                    resolve();
                },
                err => {
                    console.log(`Storage: Failed removing ${key} from secure storage: ${err}`);
                    reject(err);
                },
                key
            );
        });
    }

    /**
     * Loads the JWS token from the SecureStorage or localStorage.
     * @returns {Promise}
     */
    loadToken() {
        let promise;

        if (Storage.isMobile() && this.hasSecureStorage()) {
            promise = this.getSecureStorageItem(KEY_TOKEN);
        } else if (Storage.isBrowser() && Storage.hasLocalStorage()) {
            promise = this.getLocalStorageItem(KEY_TOKEN);
        } else {
            console.log('Error load login token, securestorage on mobile or localstorage in browser not available');
            promise = Promise.reject();
        }

        return promise.then(token => {
            if (token) {
                this.token = token;
                console.log('Storage: Loaded token from storage');
            }
        });
    }

    /**
     * Checks if this is the first launch of the app after a new install.
     * @return {Promise<Boolean>} True if first launch, otherwise false.
     */
    isFirstLaunch() {
        return new Promise(resolve => {
            File.fileExistsInDataDirectory(FILE_NAME_FIRST_LAUNCH)
                .then(exists => {
                    console.log(`Storage: First launch file ${exists ? '' : 'NOT '}exists`);
                    resolve(!exists);
                })
                .catch(() => resolve(false));
        });
    }

    /**
     * Removes any previous stored token if this is the first launch of the mobile app.
     * Always resolves the promise, even if the token was not found in the storage.
     * If we are not on a mobile device we skip the check for the first launch.
     * @return {Promise}
     */
    unsetTokenOnFirstLaunch() {
        if (!Storage.isMobile()) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            this.isFirstLaunch()
                .then(firstLaunch => {
                    if (firstLaunch) {
                        console.log('Storage: First launch of app, removing any existing tokens');

                        // set the file so we know the app has already been launched the first time
                        File.touchFileInDataDirectory(FILE_NAME_FIRST_LAUNCH)
                            .then(() => console.log('Storage: First launch file created'))
                            .catch(() => console.log('Storage: Unable to set first launch of app'));

                        return this.unsetToken();
                    } else {
                        console.log('Storage: Not first launch of app, use existing token');
                        return Promise.resolve();
                    }
                })
                .then(() => resolve())
                .catch(() => resolve());
        });
    }

    /**
     * Set JWT token.
     * @returns {Promise}
     */
    setToken(token) {
        this.token = token;
        if (Storage.isMobile() && this.hasSecureStorage()) {
            return this.setSecureStorageItem(KEY_TOKEN, token);
        } else if (Storage.isBrowser() && Storage.hasLocalStorage()) {
            return this.setLocalStorageItem(KEY_TOKEN, token);
        } else {
            console.log('Error set login token, secure storage on mobile or local storage in browser not available');
            return Promise.reject();
        }
    }

    /**
     * Get the JWT.
     */
    getToken() {
        return this.token;
    }

    /**
     * Returns the current login state.
     * @returns {Object|null} The payload of the JWT token.
     */
    getLogin() {
        try {
            return JSON.parse(atob(this.token.split('.')[1]));
        } catch (e) {
            return null;
        }
    }

    /**
     * Checks if the user is logged in.
     * @returns {Boolean} True if logged in, otherwise false.
     */
    isLoggedIn() {
        const payload = this.getLogin();

        if (!payload) {
            return false;
        }

        // new Date requires milliseconds
        return new Date(payload.exp * 1000) >= new Date();
    }

    /**
     * Removes the JWT. If the token is not available in the storage, the promise is rejected.
     * @returns {Promise}
     */
    unsetToken() {
        this.token = undefined;
        if (Storage.isMobile() && this.hasSecureStorage()) {
            return this.removeSecureStorageItem(KEY_TOKEN);
        } else if (Storage.isBrowser() && Storage.hasLocalStorage()) {
            return this.removeLocalStorageItem(KEY_TOKEN);
        } else {
            console.log('Error unset login token, securestorage on mobile or localstorage in browser not available');
            return Promise.reject();
        }
    }

    loadLocale() {
        let locale = localStorage.getItem(KEY_LOCALE);
        if (!locale || !LOCALES.includes(locale)) {
            locale = getBrowserLocale();
        }
        // this.locale = locale;
        this.locale = 'de';
    }

    setLocale(locale) {
        localStorage.setItem(KEY_LOCALE, locale);
        this.locale = locale;
    }

    getLocale() {
        return this.locale;
    }

    loadQueueMonitor() {
        const queueMonitor = JSON.parse(localStorage.getItem(KEY_QUEUEMONITOR)); // Convert string to boolean
        if (!queueMonitor) {
            this.queueMonitor = false;
        } else {
            this.queueMonitor = queueMonitor;
        }
    }

    setQueueMonitor(queueMonitor) {
        localStorage.setItem(KEY_QUEUEMONITOR, queueMonitor);
        this.queueMonitor = queueMonitor;
    }

    getQueueMonitor() {
        return this.queueMonitor;
    }

}

export default new Storage();
